/* global document, window, NodeList, requestAnimationFrame */
/* eslint no-param-reassign: ["error", { "props": false }] */
/* eslint guard-for-in: "warn" */

import swipeNav from './swipe-nav';
import selectMenu from './select-menu';

/**
* Initialise SDC Localnav Component
*
* @param rootElement {htmlNode} root dom element of component
*/
export default function(rootElement) {
  if (!rootElement) {
    return;
  }

  const navWrap = rootElement.querySelector('[data-role="nav-wrap"]');
  const navBody = rootElement.querySelector('[data-role="nav-body"]');
  const navItemWrap = rootElement.querySelector('[data-role="nav-items"]');
  let navItems = navItemWrap.querySelectorAll('[data-role="nav-item"]');
  const navItemsMoreButton = rootElement.querySelector('[data-role="nav-items-more"]');
  const navItemsMoreButtonWrap = rootElement.querySelector('[data-role="nav-items-more-wrap"]');
  let navItemsOverflow = rootElement.querySelector('[data-role="nav-items-overflow"]');
  const navHeader = rootElement.querySelector('[data-role="nav-header"]');
  const stickyNav = document.querySelector('.sdc-site-localnav--sticky');
  const stickyContent = document.querySelector('.sdc-site-localnav__sticky-content');
  const swipeNavButton = rootElement.querySelector('[data-role="nav-swipe-button"]');

  let stuck = false;

  // make a nodelist iteratable with foreach
  NodeList.prototype.forEach = Array.prototype.forEach;

  // if this is a swipe nav we can stop here
  if (swipeNavButton) {
    swipeNav(rootElement, swipeNavButton);
    return;
  }

  // generate unique id in case there are multiple instances of this component on a page
  const uniqueId = `localnav-${Math.floor(Math.random() * 1000000)}`;
  navWrap.id = uniqueId;

  // create blank style element
  const style = document.createElement('style');
  rootElement.appendChild(style);

  // create a css template
  const template = `#${uniqueId} [data-role='nav-items-overflow'] li:nth-last-of-type(-n+#{amount}) {display: flex;}`;

  // add padding right to account for more button width
  function setNavItemsOffset() {
    if (navItemWrap && navItemsMoreButton) {
      navItemWrap.style.cssText = `padding-right: ${navItemsMoreButton.offsetWidth}px`;
    }
  }

  function addClickActionToMoreButton() {
    navItemsMoreButton.onclick = (event) => {
      event.preventDefault();
      toggleMoreNav();
    };

    document.body.addEventListener('click', (event) => {
      if (!navItemsMoreButton.contains(event.target) && !navItemsOverflow.contains(event.target)) {
        toggleMoreNav('close');
      }
    });
  }

  function toggleMoreNav(close) {
    let expanded = (navItemsMoreButton.getAttribute('aria-expanded') === 'true');
    if (close) {
      expanded = true;
    }

    navItemsMoreButton.setAttribute('aria-label',
      navItemsMoreButton.getAttribute(`data-aria-label-open-${expanded}`)
    );

    if (expanded === true) {
      navItemsMoreButton.setAttribute('aria-expanded', 'false');
      navItemsOverflow.setAttribute('aria-hidden', 'true');
    } else {
      navItemsMoreButton.setAttribute('aria-expanded', 'true');
      navItemsOverflow.setAttribute('aria-hidden', 'false');
    }
  }

  function setnavItemsMoreButtonVisibility(visible) {
    if (visible) {
      navItemsMoreButtonWrap.setAttribute('aria-hidden', 'true');
      navItemsOverflow.style.visibility = 'hidden';
    } else {
      navItemsMoreButtonWrap.setAttribute('aria-hidden', 'false');
      navItemsOverflow.style.visibility = 'visible';
    }
  }

  function copyNavItemsToOverFlow() {
    navItems.forEach((item) => {
      const anchorStyle = getChildAnchorStyles(item);

      if (anchorStyle && anchorStyle.display && anchorStyle.display === 'none') {
        return;
      }

      navItemsOverflow.appendChild(preventDuplicateIdentifiers(item));
    });
  }

  function preventDuplicateIdentifiers(item) {
    const clonedItem = item.cloneNode(true);
    const link = clonedItem.querySelector('a');
    const div = clonedItem.querySelector('div');

    if (link && link.hasAttribute('aria-controls')) {
      clonedItem.querySelector('a').setAttribute('aria-controls', `${link.getAttribute('aria-controls')}-more`);
    }

    if (div && div.hasAttribute('id')) {
      clonedItem.querySelector('div').setAttribute('id', `${div.getAttribute('id')}-more`);
    }

    return clonedItem;
  }

  function getChildAnchorStyles(element) {
    const childAnchorTags = element.getElementsByTagName('a');

    if (childAnchorTags.length === 1) {
      return getComputedStyle(childAnchorTags[0], null);
    }

    return null;
  }

  function getNumberOfNonVisibleItems() {
    let overflowAmount = 0;
    navItems.forEach((item) => {
      item.setAttribute('aria-hidden', 'false');
      if (item.offsetTop > 1) {
        const anchorStyle = getChildAnchorStyles(item);

        if (anchorStyle && anchorStyle.display && anchorStyle.display === 'none') {
          return item.setAttribute('aria-hidden', 'true');
        }

        item.setAttribute('aria-hidden', 'true');
        overflowAmount += 1;
      }
    });
    return overflowAmount;
  }

  function setVisibleItems() {
    // set padding offset to account for more button
    setNavItemsOffset();

    // get number of non visible items
    const amount = getNumberOfNonVisibleItems();

    // set visiblity of more button
    navItemsMoreButton.setAttribute('aria-hidden', !amount);

    // set number of items that are visible
    setnavItemsMoreButtonVisibility(!amount);

    // set inline style to show the correct number of items using nth-of-type
    style.textContent = template.replace(/#{amount}/g, amount);
  }

  function getBreakpoint() {
    return window.getComputedStyle(navItemWrap, null).getPropertyValue('overflow');
  }

  function setNavState() {
    // add a small timeout in calling to prevent font loading delays breaking the layout calculations
    setTimeout(() => {
      requestAnimationFrame(() => {
        // check whether the nav wrap is set to overflow hidden
        // this means were in desktop view...
        // ...and we need to show the hidden items in the overflow
        const state = getBreakpoint();
        if (state.match(/hidden/)) {
          setVisibleItems();
        }
      });
    }, 500);
  }

  function toggleMobileNav(close) {
    // dont add aria states to desktop nav as it should always be visible
    if (getBreakpoint() === 'hidden') {
      return;
    }

    let expanded = (navHeader.getAttribute('aria-expanded') === 'true');
    if (close) {
      expanded = true;
    }

    navHeader.setAttribute('aria-label', navHeader.getAttribute(`data-aria-label-open-${expanded}`));

    if (expanded === true) {
      navHeader.setAttribute('aria-expanded', false);
      navBody.setAttribute('data-state-hidden', true);
    } else {
      navHeader.setAttribute('aria-expanded', true);
      navBody.setAttribute('data-state-hidden', false);
    }
  }

  function setMobileNavClick() {
    if (!navHeader) {
      return;
    }

    navHeader.onclick = (event) => {
      if (getBreakpoint() !== 'hidden') {
        event.preventDefault();
        toggleMobileNav();
      }
    };

    document.body.addEventListener('click', (event) => {
      if (!navHeader.contains(event.target) && !navBody.contains(event.target)) {
        toggleMobileNav('close');
      }
    });
  }

  function setHashLinks() {
    const hashLinks = rootElement.querySelectorAll('a[data-hash]');

    hashLinks.forEach((item) => setHashLinkOnClick(item));
  }

  function setHashLinkOnClick(item) {
    item.onclick = (event) => {
      const hashLinks = rootElement.querySelectorAll('a[data-hash]');
      const hashTargets = document.querySelectorAll('[data-hash-target]');

      // eslint-disable-next-line curly
      if (event) event.preventDefault();

      // get hash of link
      const hash = item.getAttribute('data-hash');

      // remove aria current off all links
      hashLinks.forEach((link) => {
        link.removeAttribute('aria-current');
      });

      // hide all hashTargets
      hashTargets.forEach((target) => {
        target.setAttribute('aria-hidden', 'true');
      });

      // add aria-current to clicked link
      item.setAttribute('aria-current', 'true');

      // show selected target
      const target = document.getElementById(hash);
      if (target) {
        target.setAttribute('aria-hidden', 'false');
      }

      // close the more nav
      toggleMoreNav('close');
    };
  }

  function setStickyNav() {
    if (stickyNav) {
      scrollToAnchor();
      toggleStickyNav(stickyNav, stickyContent);
      setScrollSpy(stickyContent);
    }
  }

  function scrollToAnchor() {
    const links = document.querySelectorAll('[data-role="nav-item-links"]');

    links.forEach((link) => {
      link.onclick = (event) => {
        const element = document.getElementById(event.target.getAttribute('href').substring(1));
        const to = element.offsetTop - 65;
        const from = document.documentElement.scrollTop || document.body.scrollTop || window.pageYOffset;

        navItemsOverflow.setAttribute('aria-hidden', 'true');
        navItemsMoreButton.setAttribute('aria-expanded', 'false');

        if (to !== from) {
          smoothScroll(document.scrollingElement || document.documentElement, from, to, 200);
        } else {
          event.preventDefault();
        }
      };
    });
  }

  function smoothScroll(element, from, to, time) {
    if (!element) {
      return;
    }

    const start = new Date().getTime();
    const timer = setInterval(() => {
      const step = Math.min(1, (new Date().getTime() - start) / time);
      element.scrollTop = (from + step * (to - from));
      if (step === 1) {
        clearInterval(timer);
      }
    }, 25);
    element.scrollTop = from;
  }

  function toggleStickyNav(stickyElement, stickyElementContent) {
    requestAnimationFrame(() => {
      const topEdge = stickyElement.getBoundingClientRect().top;

      if (topEdge <= 0) {
        stickyElementContent.setAttribute('data-stuck', 'true');
        stuck = true;
      } else {
        stickyElementContent.removeAttribute('data-stuck');
        stuck = false;
      }
    });
  }

  function setScrollSpy(item) {
    requestAnimationFrame(() => {
      const articleSections = document.querySelectorAll('[data-role="localnav-item"]');
      const navCurrentSection = document.querySelector('.sdc-site-localnav__current-section-name');
      const scrollPosition = document.documentElement.scrollTop || document.body.scrollTop;
      const spyPoints = {};

      if (navCurrentSection && articleSections[0]) {
        navCurrentSection.textContent = articleSections[0].textContent;
      }

      if (!stuck || articleSections.length <= 0 || !navCurrentSection) {
        return;
      }

      Array.prototype.forEach.call(articleSections, (e) => {
        spyPoints[e.id] = e.offsetTop - (item.offsetHeight + 25);
      });

      for (const id in spyPoints) { // eslint-disable-line guard-for-in
        const current = item.querySelector('a[aria-current=true]');
        const link = item.querySelector(`a[href*="${id}"]`);

        if (spyPoints[id] <= scrollPosition && link) {
          if (current) {
            current.removeAttribute('aria-current');
            navCurrentSection.textContent = link.textContent;
          }
          link.setAttribute('aria-current', true);
        }
      }
    });
  }

  // set resize variable
  let resize = false;

  window.addEventListener('resize', () => {
    if (resize) {
      return;
    }

    setTimeout(() => {
      setNavState();
      if (stickyNav) {
        setScrollSpy(stickyContent);
        toggleStickyNav(stickyNav, stickyContent);
      }
      resize = false;
    }, 250);

    resize = true;
  });

  if (stickyNav) {
    // set scroll variable
    let scroll = false;

    window.addEventListener('scroll', () => {
      if (scroll) {
        return;
      }

      setTimeout(() => {
        setScrollSpy(stickyContent);
        toggleStickyNav(stickyNav, stickyContent);
        scroll = false;
      }, 1000 / 60);

      scroll = true;
    });
  }

  copyNavItemsToOverFlow();
  addClickActionToMoreButton();
  setNavState();
  setMobileNavClick();
  setHashLinks();
  setStickyNav();

  selectMenu(navItemWrap);
  selectMenu(navItemsOverflow);

  function addItem(navItemData) {
    const html = `
      <a href="${navItemData.link}" class="sdc-site-localnav__item-link" data-role="nav-item-links">
        <span class="u-hide-visually">${navItemData.a11yLabel}</span>
        <span aria-hidden="true">${navItemData.label}</span>
      </a>
    `;

    const listItem = document.createElement('li');
    listItem.className = 'sdc-site-localnav__item';
    listItem.setAttribute('data-role', 'nav-item');
    listItem.innerHTML = html;

    if (navItemData.hash) {
      listItem.setAttribute('href', `#${navItemData.hash}`);
      listItem.firstElementChild.setAttribute('data-hash', navItemData.hash);
      setHashLinkOnClick(listItem.firstElementChild);
    }

    navItemsOverflow.appendChild(preventDuplicateIdentifiers(listItem));
    navItemWrap.appendChild(listItem);

    navItems = navItemWrap.querySelectorAll('[data-role="nav-item"]');
    navItemsOverflow = rootElement.querySelector('[data-role="nav-items-overflow"]');

    return listItem;
  }

  function revealWith(additionalProperties) {
    rootElement.classList.remove('u-hide-visually');
    rootElement.removeAttribute('aria-hidden');
    rootElement.classList.add(additionalProperties.class);
  }

  rootElement.addItem = addItem;
  rootElement.revealWith = revealWith;
}
